import React, { useEffect, useState, useRef, memo } from 'react';
import { gameProgressState, tigerState, gameState } from '../../state';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import styled from 'styled-components';
import { GameObject } from './GameObject';
import { getGameObjectWithSeed } from './GameObject';
import { TIGER_IMAGES } from '../common/images';

const BACKGROUND_HEIGHT = 30000;

const StyledGameWrapper = styled.div`
  height: 100vh;
  max-height: 100vh;
  width: 100%;
  position: relative;
`;

const Background = styled.div.attrs(({ y }) => ({
  style: {
    transform: `translateY(${y}px)`,
    transitionDuration: `${y === 0 ? 0 : 0.2}s`
  },
}))`
  background: url('img/game-bg1920.jpg') repeat;
  background-position: center;
  overflow: hidden;
  height: ${BACKGROUND_HEIGHT}px;
  bottom: 0px;
  width: 100%;
  position: absolute;
  transition-property: transform;
  transition-timing-function: linear;
  transition-delay: 0s;
  display: flex;
  justify-content: center;
`

const ObjetContainer = styled.div`
  height: 100%;
  width: 80%;
  max-width: 640px;
  position: relative;
`

const StyledTigerControl = styled.div`
  position: fixed;
  bottom: 0px;
  width: 100%;
  height: 150px;
  display: flex;
  justify-content: center;
`

const StyledStatusBar = styled.div`
  position: fixed;
  align-items: center;
  letter-spacing: 3px;
  color: #ffffff;
  top: 10px;
  width: 100%;
  max-width:640px;
  height: 100px;
  display: flex;
  justify-content: space-between;
  left: 50%;
  transform: translate(-50%, 0);;
  z-index: 1;
  font-size: 36px;
  @media only screen and (max-width: 600px) {
    font-size: 24px;
    letter-spacing:0;
  }
`
const StyledScoreBar = styled.div`
color: #f8f9c5;
font-size: 36px;
background-color:#C1671D;
border-radius: 20px;
box-shadow: 0 10px #a25019;
max-height:60px;
padding: 5px 30px;
margin:5px 10px;
@media only screen and (max-width: 600px) {
  max-width:30%;
  padding: 5px 10px;
  font-size: 24px;
  letter-spacing:0;

}
`

const TigerMovementContainer = styled.div`
  width: 80%;
  max-width: 640px;
  position: relative;
`

const Tiger = styled.img.attrs(({ x }) => ({
  style: {
    transform: `translateX(${x-40}px)`,
    transitionDuration: `${x === 0 ? 0 : 0.25}s`
  }
}))`
  width: 80px;
  position: absolute;
`
const StyledLifeCounter = styled.div`
top:0;
bottom:0;
margin:auto;
display: flex;
@media only screen and (max-width: 600px) {
  max-width:30%;
}
`
const StyledControlButton = styled.div`
display:flex;
position:fixed;
bottom:0;
justify-content: space-between;
width: 100%;
max-width: 640px;
left: 50%;transform: translate(-50%, 0);
@media only screen and (max-width: 600px) {

}
`
const ControlButton = styled.img`
max-height: 100px;
padding:30px;
max-width:15%;
`


const StyledLife = styled.img`
max-height: 50px;
padding:3px;
@media only screen and (max-width: 600px) {
  max-width:30%;
}
`

// toys must sort by y in asc order
const makeGameObjects = () => ([...Array(400)].map((_, i) => {
  const trap = i % 5 === 0;
  const objectType = getGameObjectWithSeed(trap, Math.floor(Math.random() * 100));
  var objectX=320;
  if(objectType === getGameObjectWithSeed(1,0)){
    objectX =(i * 210) % 640;
  }else if(objectType === getGameObjectWithSeed(1,1)){
    objectX = 240 + (Math.random() * 10000) % 400;
  }else {
    objectX = (Math.random() * 10000) % 640;
  }

  return {
    id: '_' + i,
    trap,
    objectType,
    y: objectType === getGameObjectWithSeed(1,0)? i * 80 - ((i *80)%960) + 590 :i * 80,
    x: objectX
  };
}));

const TigerControl = React.memo(({ hitTrap }) => {
  const [tiger, setTiger] = useRecoilState(tigerState);
  const [width, setWidth] = useState(-1);
  const ref = useRef(null);

  useEffect(() => {
    setWidth(ref.current.clientWidth);
  }, [setWidth, ref]);

  useEffect(() => {
    if (width < 0) {
      return;
    }
    const speed = 60;
    let lastAt = new Date();
    const handleKeyDown = (event) => {
      const now = new Date()
      if (now - lastAt < 200) {
        return
      }
      lastAt = now;
      switch (event.keyCode) {
        case 37: // left
          setTiger((tiger) => ({ ...tiger, x: Math.max(10, tiger.x - speed) }))
          break;
        case 39:
          setTiger((tiger) => ({ ...tiger, x: Math.min(width -10 , tiger.x + speed) }))
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setTiger, width])

  return (

    <StyledTigerControl>
      <TigerMovementContainer ref={ref}>
        <Tiger src={hitTrap ? TIGER_IMAGES.hitTrap : TIGER_IMAGES.default} x={tiger.x} />
      </TigerMovementContainer>
    </StyledTigerControl>
  );
})

const ClickLeft = () =>{
  var event = new KeyboardEvent('keydown', {
    keyCode: 37
  });
  document.dispatchEvent(event);
}
const ClickRight = () =>{
  var event = new KeyboardEvent('keydown', {
    keyCode: 39
  });
  document.dispatchEvent(event);
}

const Life = memo(({ alive }) => <StyledLife src={alive ? 'img/life.png' : 'img/nonlife.png'} />)

const LifeCounter = memo(({ lifeCount }) => {
  return (
    <StyledLifeCounter>
      <Life alive={lifeCount > 0} />
      <Life alive={lifeCount > 1} />
      <Life alive={lifeCount > 2} />
    </StyledLifeCounter>
  );
})

const StatusBar = memo(() => {
  const totalLife = 3
  const [clock, setClock] = useState(0);
  const setGameProgress = useSetRecoilState(gameProgressState);
  const game = useRecoilValue(gameState);

  useEffect(() => {
    const startAt = Date.now();

    const interval = setInterval(() => {
      const seconds = 90 - (Math.floor((Date.now() - startAt) / 1000));
      if (seconds < 1) {
        setGameProgress('result');
      } else {
        setClock(seconds);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [setClock, setGameProgress]);

  useEffect(() => {
    if (game.life.size >= totalLife) {
      document.getElementById('audio').pause();
      setGameProgress('result');
    }
  }, [game, setGameProgress]);

  return (
    <StyledStatusBar>
      <LifeCounter lifeCount={totalLife - game.life.size} />
      <b>0{Math.floor(clock / 60)}：{(clock % 60)<10?"0":""}{clock % 60}</b>
      <StyledScoreBar>
        <b>分數&ensp;{game.score.size < 100 ? "0" : ""}{game.score.size < 10 ? "0" : ""}{game.score.size}</b>
      </StyledScoreBar>
    </StyledStatusBar>
  );
});

const findMatchingObjects = (gameObjects, backgroundY, tigerX) => {
  const matches = [];
  let clearCount = 0;
  for (let i = gameObjects.length - 1; i > 0; i--) {
    const gameObject = gameObjects[i]
    const gameObjectY = BACKGROUND_HEIGHT - backgroundY - gameObject.y;
    if (gameObjectY > 200) {
      break;
    }
    if (gameObjectY > 50) {
      const gameObjetX = gameObject.x;
      const gameObjectWidth = gameObject.objectType.width;
      if (Math.abs(tigerX - gameObjetX) <= (40 + gameObjectWidth / 2)) {
        matches.push(gameObject);
      }
    } else {
      clearCount++;
    }
  }
  for (let i = 0; i < clearCount; i++) {
    gameObjects.pop();
  }

  return matches
}

const BaseGame = ({ gameObjects }) => {
  const [game, setGameState] = useRecoilState(gameState);
  const tiger = useRecoilValue(tigerState);
  const [backgroundY, setBackgroundY] = useState(0);
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    const startAt = Date.now();
    const interval = setInterval(() => {
      const seconds = 90 - (Math.floor((Date.now() - startAt) / 1000));
      setBackgroundY((y) => {
        let speed=0;
        if(seconds < 30){
          speed = 70;
        }else if(seconds < 60){
          speed = 60;
        }else if(seconds < 80){
          speed = 50;
        }else{
          speed = 40;
        }
        const nextY = y + speed;

        return nextY;
      })
    }, 200);

    return () => clearInterval(interval);
  }, [setBackgroundY]);

  useEffect(() => {
    const matched = findMatchingObjects(gameObjects, backgroundY, tiger.x);
    if (matched.length > 0) {
      const diff = matched.reduce((agg, { id, trap }) => {
        if (trap) {
          agg.life.add(id);
        } else {
          agg.score.add(id);
        }
        return agg
      }, {
        life: new Set(),
        score: new Set()
      });

      setGameState(({ score, life }) => ({
        score: new Set([...score, ...diff.score]),
        life: new Set([...life, ...diff.life])
      }));

      setMatches(matched);
    } else {
      setMatches([])
    }
  }, [setMatches, setGameState, backgroundY, tiger.x])

  return (
    <>
      <StatusBar />
      <StyledGameWrapper>
        <Background y={backgroundY}>
          <ObjetContainer>
            {
              gameObjects.map((gameObject) => {
                if (game.life.has(gameObject.id) || game.score.has(gameObject.id)) {
                  return null
                }

                const isMatch = matches.includes(gameObject);

                const gameObjectY = BACKGROUND_HEIGHT - backgroundY - gameObject.y;
                const CarMove = gameObject.objectType === getGameObjectWithSeed(1,0)? 10:0;
                const DogMove = gameObject.objectType === getGameObjectWithSeed(1,1)? 20:0;
                if (gameObjectY < 1600) {
                  gameObject.x = (640 + gameObject.x - CarMove)%640;
                }
                if (gameObjectY < 500 && gameObjectY > 420){
                  gameObject.x = (640 + gameObject.x - DogMove)%640;
                }


                return (
                  <GameObject
                    key={gameObject.id}
                    objectType={gameObject.objectType}
                    y={gameObject.y}
                    x={gameObject.x}
                    match={isMatch}
                  />
                );
              })
            }
          </ObjetContainer>
        </Background>
      </StyledGameWrapper>
      <TigerControl hitTrap={!matches.every(({ trap })=> !trap)} />
      <StyledControlButton>
        <ControlButton src={'img/control-left.png'} onClick={ClickLeft}/>
        <ControlButton src={'img/control-right.png'} onClick={ClickRight}/>
      </StyledControlButton>
    </>
  );
};

const Game = () => {
  const [gameObjects, setGameObjects] = useState(null);
  useEffect(() => {
    setGameObjects(makeGameObjects());
  }, [setGameObjects])

  if (!gameObjects) {
    return null;
  }

  return (
    <BaseGame gameObjects={gameObjects} />
  )
}

export {
  Game
}
